import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  Download, Trash2, Mail, Phone, MapPin, MessageCircle, Search,
  ChevronLeft, ChevronRight, Clock, Stethoscope, LayoutGrid, List as ListIcon, Building2,
} from "lucide-react";

const STAGES = [
  { key: "new", label: "New", color: "#0D9488" },
  { key: "contacted", label: "Contacted", color: "#1E3A8A" },
  { key: "scheduled", label: "Scheduled", color: "#f59e0b" },
  { key: "closed", label: "Won / Closed", color: "#10B981" },
];
const COLORS = Object.fromEntries(STAGES.map((s) => [s.key, s.color]));
const STAGE_KEYS = STAGES.map((s) => s.key);

const timeAgo = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const secs = Math.floor((Date.now() - d.getTime()) / 1000);
  if (secs < 60) return "just now";
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return d.toLocaleDateString();
};

const digits = (p) => (p || "").replace(/[^\d]/g, "");
const waHref = (lead) => {
  const d = digits(lead.phone);
  const first = (lead.name || "").split(" ")[0];
  const proc = lead.procedure ? ` about your ${lead.procedure} request` : " about your quote request";
  const msg = encodeURIComponent(`Hi ${first}, this is My Global Dentist reaching out${proc}. When is a good time to talk?`);
  return d ? `https://wa.me/${d}?text=${msg}` : null;
};

export default function LeadsAdmin() {
  const [leads, setLeads] = useState([]);
  const [view, setView] = useState("pipeline");
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [notes, setNotes] = useState("");

  const load = () => {
    api.get("/admin/leads").then((r) => setLeads(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const updateStatus = async (lead, status) => {
    await api.put(`/admin/leads/${lead.id}`, { status });
    setLeads((prev) => prev.map((l) => (l.id === lead.id ? { ...l, status } : l)));
    if (selected?.id === lead.id) setSelected({ ...selected, status });
    toast.success(`Moved to ${STAGES.find((s) => s.key === status)?.label}`);
  };

  const move = (lead, dir) => {
    const i = STAGE_KEYS.indexOf(lead.status);
    const next = STAGE_KEYS[Math.min(Math.max(i + dir, 0), STAGE_KEYS.length - 1)];
    if (next !== lead.status) updateStatus(lead, next);
  };

  const saveNotes = async () => {
    await api.put(`/admin/leads/${selected.id}`, { notes });
    setLeads((prev) => prev.map((l) => (l.id === selected.id ? { ...l, notes } : l)));
    setSelected({ ...selected, notes });
    toast.success("Notes saved");
  };

  const remove = async (id) => {
    await api.delete(`/admin/leads/${id}`);
    setLeads((prev) => prev.filter((l) => l.id !== id));
    setSelected(null);
    toast.success("Lead deleted");
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return leads.filter((l) => {
      if (typeFilter !== "all" && l.type !== typeFilter) return false;
      if (!q) return true;
      return [l.name, l.email, l.phone, l.procedure, l.country, l.clinic_name]
        .some((f) => (f || "").toString().toLowerCase().includes(q));
    });
  }, [leads, search, typeFilter]);

  const grouped = useMemo(() => {
    const g = Object.fromEntries(STAGE_KEYS.map((k) => [k, []]));
    filtered.forEach((l) => { (g[l.status] || g.new).push(l); });
    return g;
  }, [filtered]);

  const exportCsv = () => {
    const headers = ["Name", "Email", "Phone", "Country", "Procedure", "Best contact", "Best time", "Type", "Status", "Clinic", "Notes", "Created"];
    const rows = filtered.map((l) => [l.name, l.email, l.phone, l.country, l.procedure, l.best_contact, l.best_time, l.type, l.status, l.clinic_name || "", l.notes || "", l.created_at]);
    const csv = [headers, ...rows].map((r) => r.map((c) => `"${(c || "").toString().replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "leads.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const openLead = (l) => { setSelected(l); setNotes(l.notes || ""); };

  const QuickActions = ({ lead, size = "sm" }) => {
    const wa = waHref(lead);
    const cls = "rounded-full h-8 w-8";
    return (
      <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
        {wa && (
          <a href={wa} target="_blank" rel="noreferrer" title="WhatsApp">
            <Button variant="outline" size="icon" className={cls} data-testid={`lead-wa-${lead.id}`}>
              <MessageCircle className="w-4 h-4 text-[#25D366]" />
            </Button>
          </a>
        )}
        {lead.phone && (
          <a href={`tel:${lead.phone}`} title="Call">
            <Button variant="outline" size="icon" className={cls} data-testid={`lead-call-${lead.id}`}>
              <Phone className="w-4 h-4 text-primary" />
            </Button>
          </a>
        )}
        <a href={`mailto:${lead.email}?subject=${encodeURIComponent("Your My Global Dentist quote")}`} title="Email">
          <Button variant="outline" size="icon" className={cls} data-testid={`lead-email-${lead.id}`}>
            <Mail className="w-4 h-4 text-secondary" />
          </Button>
        </a>
      </div>
    );
  };

  const LeadCard = ({ lead }) => {
    const i = STAGE_KEYS.indexOf(lead.status);
    return (
      <div
        role="button"
        tabIndex={0}
        onClick={() => openLead(lead)}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openLead(lead); } }}
        className="w-full text-left cursor-pointer bg-white rounded-xl border border-border p-3.5 hover:shadow-md hover:border-primary/40 transition-all"
        data-testid={`lead-card-${lead.id}`}
      >
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="font-semibold text-sm truncate">{lead.name}</p>
            <p className="text-xs text-muted-foreground truncate">{lead.email}</p>
          </div>
          <span className={`shrink-0 text-[10px] font-bold uppercase tracking-wide rounded-full px-2 py-0.5 ${lead.type === "call" ? "bg-secondary/10 text-secondary" : "bg-primary/10 text-primary"}`}>
            {lead.type}
          </span>
        </div>
        <div className="mt-2 space-y-1 text-xs text-muted-foreground">
          <p className="flex items-center gap-1.5"><Stethoscope className="w-3.5 h-3.5" /> {lead.procedure || "General inquiry"}</p>
          {lead.clinic_name && <p className="flex items-center gap-1.5 truncate"><Building2 className="w-3.5 h-3.5 shrink-0" /> {lead.clinic_name}</p>}
          {lead.country && <p className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" /> {lead.country}</p>}
          <p className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> {timeAgo(lead.created_at)}</p>
        </div>
        <div className="mt-3 flex items-center justify-between border-t border-border pt-2.5">
          <QuickActions lead={lead} />
          <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
            <Button variant="ghost" size="icon" className="h-8 w-8 rounded-full" disabled={i <= 0} onClick={() => move(lead, -1)} data-testid={`lead-prev-${lead.id}`}>
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="icon" className="h-8 w-8 rounded-full" disabled={i >= STAGE_KEYS.length - 1} onClick={() => move(lead, 1)} data-testid={`lead-next-${lead.id}`}>
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold">Sales Pipeline</h1>
          <p className="text-muted-foreground mt-1">Work every lead from New → Won. Reach out in one click.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, email, procedure…" className="pl-9 h-11 w-64" data-testid="leads-search" />
          </div>
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="w-32 h-11" data-testid="leads-type-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All types</SelectItem>
              <SelectItem value="quote">Quote</SelectItem>
              <SelectItem value="call">Call</SelectItem>
            </SelectContent>
          </Select>
          <div className="flex rounded-full border border-border overflow-hidden">
            <button onClick={() => setView("pipeline")} className={`px-3 h-11 flex items-center gap-1.5 text-sm ${view === "pipeline" ? "bg-primary text-white" : "bg-white text-muted-foreground"}`} data-testid="leads-view-pipeline">
              <LayoutGrid className="w-4 h-4" /> Pipeline
            </button>
            <button onClick={() => setView("list")} className={`px-3 h-11 flex items-center gap-1.5 text-sm ${view === "list" ? "bg-primary text-white" : "bg-white text-muted-foreground"}`} data-testid="leads-view-list">
              <ListIcon className="w-4 h-4" /> List
            </button>
          </div>
          <Button variant="outline" className="rounded-full h-11" onClick={exportCsv} data-testid="leads-export-btn"><Download className="w-4 h-4 mr-2" /> Export</Button>
        </div>
      </div>

      {view === "pipeline" ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 mt-6">
          {STAGES.map((stage) => (
            <div key={stage.key} className="bg-muted/50 rounded-2xl p-3" data-testid={`pipeline-col-${stage.key}`}>
              <div className="flex items-center justify-between px-1 pb-3">
                <div className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full" style={{ background: stage.color }} />
                  <h3 className="font-heading font-bold text-sm">{stage.label}</h3>
                </div>
                <span className="text-xs font-semibold rounded-full px-2 py-0.5 bg-white text-muted-foreground" data-testid={`pipeline-count-${stage.key}`}>
                  {grouped[stage.key].length}
                </span>
              </div>
              <div className="space-y-2.5 min-h-[80px]">
                {grouped[stage.key].length === 0 && <p className="text-xs text-muted-foreground text-center py-6">No leads</p>}
                {grouped[stage.key].map((l) => <LeadCard key={l.id} lead={l} />)}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <Card className="rounded-2xl overflow-hidden mt-6">
          <div className="hidden md:grid grid-cols-12 bg-muted px-5 py-3 text-xs uppercase tracking-wider text-muted-foreground font-semibold">
            <div className="col-span-3">Lead</div><div className="col-span-3">Procedure</div><div className="col-span-2">Received</div><div className="col-span-2">Stage</div><div className="col-span-2 text-right">Reach out</div>
          </div>
          {filtered.length === 0 && <p className="text-center text-muted-foreground py-16">No leads found.</p>}
          {filtered.map((l) => (
            <div key={l.id} className="grid grid-cols-2 md:grid-cols-12 gap-2 px-5 py-4 border-t border-border items-center text-sm" data-testid={`lead-row-${l.id}`}>
              <button className="col-span-2 md:col-span-3 text-left" onClick={() => openLead(l)} data-testid={`lead-open-${l.id}`}>
                <p className="font-medium hover:text-primary">{l.name}</p>
                <p className="text-xs text-muted-foreground">{l.email}</p>
              </button>
              <div className="col-span-1 md:col-span-3 text-muted-foreground">{l.procedure || "General"} · <span className="capitalize">{l.type}</span></div>
              <div className="col-span-1 md:col-span-2 text-muted-foreground text-xs">{timeAgo(l.created_at)}</div>
              <div className="col-span-1 md:col-span-2">
                <Select value={l.status} onValueChange={(v) => updateStatus(l, v)}>
                  <SelectTrigger className="h-9 w-32" data-testid={`lead-status-${l.id}`}><SelectValue /></SelectTrigger>
                  <SelectContent>{STAGES.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="col-span-1 md:col-span-2 flex md:justify-end"><QuickActions lead={l} /></div>
            </div>
          ))}
        </Card>
      )}

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-lg" data-testid="lead-detail-dialog">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading text-2xl flex items-center gap-3">
                  {selected.name}
                  <span className="text-xs rounded-full px-2.5 py-1" style={{ background: `${COLORS[selected.status]}1a`, color: COLORS[selected.status] }}>{STAGES.find((s) => s.key === selected.status)?.label}</span>
                </DialogTitle>
                <DialogDescription>Received {timeAgo(selected.created_at)} · {selected.type} request</DialogDescription>
              </DialogHeader>

              <div className="flex flex-wrap gap-2">
                {waHref(selected) && (
                  <a href={waHref(selected)} target="_blank" rel="noreferrer" className="flex-1 min-w-[120px]">
                    <Button className="w-full rounded-full bg-[#25D366] hover:bg-[#20bd5a]" data-testid="lead-detail-wa"><MessageCircle className="w-4 h-4 mr-2" /> WhatsApp</Button>
                  </a>
                )}
                {selected.phone && (
                  <a href={`tel:${selected.phone}`} className="flex-1 min-w-[100px]">
                    <Button variant="outline" className="w-full rounded-full" data-testid="lead-detail-call"><Phone className="w-4 h-4 mr-2" /> Call</Button>
                  </a>
                )}
                <a href={`mailto:${selected.email}?subject=${encodeURIComponent("Your My Global Dentist quote")}`} className="flex-1 min-w-[100px]">
                  <Button variant="outline" className="w-full rounded-full" data-testid="lead-detail-email"><Mail className="w-4 h-4 mr-2" /> Email</Button>
                </a>
              </div>

              <div className="space-y-2 text-sm">
                <p className="flex items-center gap-2"><Mail className="w-4 h-4 text-muted-foreground" /> <a href={`mailto:${selected.email}`} className="text-primary">{selected.email}</a></p>
                {selected.phone && <p className="flex items-center gap-2"><Phone className="w-4 h-4 text-muted-foreground" /> {selected.phone}</p>}
                {selected.country && <p className="flex items-center gap-2"><MapPin className="w-4 h-4 text-muted-foreground" /> {selected.country}</p>}
                <div className="grid grid-cols-2 gap-3 pt-2">
                  <div className="rounded-lg bg-muted p-3"><p className="text-xs text-muted-foreground">Procedure</p><p className="font-medium">{selected.procedure || "—"}</p></div>
                  <div className="rounded-lg bg-muted p-3"><p className="text-xs text-muted-foreground">Best contact</p><p className="font-medium">{selected.best_contact || "—"}</p></div>
                  <div className="rounded-lg bg-muted p-3"><p className="text-xs text-muted-foreground">Best time to call</p><p className="font-medium">{selected.best_time || "—"}</p></div>
                  {selected.budget && <div className="rounded-lg bg-muted p-3"><p className="text-xs text-muted-foreground">Budget</p><p className="font-medium">{selected.budget}</p></div>}
                </div>
                {selected.clinic_name && <p className="text-sm"><span className="text-muted-foreground">Clinic:</span> {selected.clinic_name}</p>}
                {selected.message && <div className="rounded-lg bg-muted p-3"><p className="text-xs text-muted-foreground mb-1">Message</p><p>{selected.message}</p></div>}
                {selected.xray_url && <p className="text-sm"><a href={selected.xray_url} target="_blank" rel="noreferrer" className="text-primary underline">View x-ray/photo →</a></p>}
              </div>

              <div className="space-y-2">
                <p className="text-sm font-medium">Follow-up notes</p>
                <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} placeholder="Add internal notes…" data-testid="lead-notes-input" />
                <Button className="rounded-full" onClick={saveNotes} data-testid="lead-save-notes-btn">Save notes</Button>
              </div>

              <div className="flex flex-wrap gap-2 border-t border-border pt-3">
                {STAGES.map((s) => (
                  <Button key={s.key} variant={selected.status === s.key ? "default" : "outline"} size="sm" className="rounded-full" onClick={() => updateStatus(selected, s.key)} data-testid={`lead-set-${s.key}`}>{s.label}</Button>
                ))}
                <Button variant="ghost" size="sm" className="rounded-full text-destructive ml-auto" onClick={() => remove(selected.id)} data-testid="lead-detail-delete"><Trash2 className="w-4 h-4 mr-1.5" /> Delete</Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
